import React, { useState } from 'react';
import { Snackbar, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ThumbDownOffAltOutlinedIcon from '@mui/icons-material/ThumbDownOffAltOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useGenieLogMutation } from '../store/services/chatlog';
import { selectUserId } from '../store/features/authSlice';
import { useSelector } from 'react-redux';
import { selectConversationId, selectSpaceId } from '../store/features/genieSlice';

interface IGenieCopyToClipboard {
  id: string;
  result: any
}

const GenieCopyToClipboard: React.FC<IGenieCopyToClipboard> = ({ id, result }) => {
  const styles = useStyles()
  const [genieLog] = useGenieLogMutation()
  const user_id = useSelector(selectUserId)
  const [openSnack, setOpenSnack] = useState(false)
  const [selectedThumb, setSelectedThumb] = useState('')
  const [message, setMessage] = useState('')
  const handleToggleSnack = () => setOpenSnack(b => !b)
  const convid = useSelector(selectConversationId)
  const spaceid = useSelector(selectSpaceId)
  
  const copyFn = async (target: string) => {
    try {
      setMessage('Content copied to clipboard')
      const data = result?.statement_response?.result?.data_array || []
      let htmlContent = ''
      let plainContent = ''

      if (Array.isArray(data) && data.length > 0) {
        // Always convert array data to CSV format
        const headers = result?.statement_response?.manifest?.schema?.columns.map((column: any) => column.name) || []
        
        // Helper function to escape and quote CSV values
        const escapeCsvValue = (value: any) => {
          if (value === null || value === undefined) return '';
          const stringValue = String(value).trim();
          
          // Check if the value is numeric and format with commas
          const numValue = Number(stringValue);
          let formattedValue = stringValue;
          
          if (!isNaN(numValue) && isFinite(numValue)) {
            formattedValue = Number.isInteger(numValue) 
              ? numValue.toLocaleString() 
              : numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }
          
          // If value contains quotes, commas, or newlines, wrap in quotes and escape existing quotes
          if (formattedValue.includes('"') || formattedValue.includes(',') || formattedValue.includes('\n')) {
            return `"${formattedValue.replace(/"/g, '""')}"`;
          }
          return formattedValue;
        };

        const csvContent = [
          headers.map(escapeCsvValue).join(','),
          ...data.map((row: any) => row.map(escapeCsvValue).join(','))
        ].join('\n');
        
        plainContent = csvContent;
        htmlContent = `<pre>${csvContent}</pre>`;
      } else {
        // For non-tabular data, preserve HTML formatting
        const content = JSON.stringify(result, null, 2);
        plainContent = content;
        htmlContent = `<pre>${content}</pre>`;
      }

      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([htmlContent], { type: "text/html" }),
          "text/plain": new Blob([plainContent], { type: "text/plain" })
        })
      ]);

      handleToggleSnack()
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      setMessage('Failed to copy to clipboard');
      handleToggleSnack();
    }
  };

  const handleThumbClick = (thumb: string) => {
    setSelectedThumb(thumb)

    console.log('------------>>>>>>>>', result)

    genieLog({
      email: user_id,
      question: result?.question,
      sql: result?.attachment?.query?.query,
      response: JSON.stringify(result?.statement_response?.result?.data_array || []),
      source: thumb,
      sql_explanation: '', //result?.attachment?.query?.description
      conversation_id: convid || '',
      space_id: spaceid || ''
    })
  }


  return (
    <>
      <hr />
      <div style={{ paddingTop: 10, marginBottom: 40, display: 'flex', wordSpacing: 20 }}>
        <Tooltip title="copy" className={styles.copyIconStyle} onClick={() => copyFn(id.toString())}>
          <ContentCopyOutlinedIcon />
        </Tooltip>
        <Tooltip title="positive" className={styles.copyIconStyle} onClick={() => handleThumbClick('positive')}>
          {selectedThumb === 'positive' ? <ThumbUpIcon /> : <ThumbUpAltOutlinedIcon />}
        </Tooltip>
        <Tooltip title="negative" className={styles.copyIconStyle} onClick={() => handleThumbClick('negative')}>
          {selectedThumb === 'negative' ? <ThumbDownIcon /> : <ThumbDownOffAltOutlinedIcon />}
        </Tooltip>
      </div>
      <Snackbar
        open={openSnack}
        autoHideDuration={2000}
        onClose={handleToggleSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={message}
      />
    </>)
}

const useStyles = makeStyles(() => ({
  copyIconStyle: {
    marginRight: 10,
    '&:hover': {
      fill: 'green',
      Opacity: 0.5,
      color: 'green'
    }
  }
}))

export default GenieCopyToClipboard